import {
  AUDIENCE_LABELS,
  FAMILY_LABELS,
  GLASSES_TYPE_LABELS,
  SIZE_LABELS,
  frameShapeLabel,
  type Audience,
  type FrameShape,
  type GlassesType,
} from "../../catalog/taxonomy";
import {
  discountPercent,
  parseStock,
  pricesFor,
  type GlassesDetails,
} from "../../import/glassesDetails";
import { ROLE_LABELS, SOURCE_ROLES, type ParsedGlassesFolder } from "../../import/glassesFolderParser";
import { formatMoney } from "../../components/ui";
import type { ResolvedColor } from "./ProductDetailsStep";

/**
 * The last look before anything is created or sent.
 *
 * Everything the operator decided, restated in one place in their own words —
 * including the things they decided by not changing them. The point is that a
 * mistake made three steps ago is visible here, while it is still free.
 *
 * Nothing on this screen is a link into the technical record: no batch id, no
 * revision number, no object key. Those exist, and none of them answers a
 * question a shop administrator is asking at this moment.
 */
export function ImportSummary({
  parse,
  colors,
  details,
  audience,
  glassesType,
  frameShape,
  onBack,
  onEdit,
  onStart,
  starting,
}: {
  parse: ParsedGlassesFolder;
  colors: readonly ResolvedColor[];
  details: GlassesDetails;
  audience: Audience;
  glassesType: GlassesType;
  frameShape: FrameShape;
  onBack: () => void;
  onEdit: () => void;
  onStart: () => void;
  /** True from the first click until the import owns the screen, so a second
   *  click cannot open a second batch or create a second product. */
  starting: boolean;
}) {
  const { base, sale } = pricesFor(details.pricing);
  const discount = details.pricing.onSale ? discountPercent(base, sale) : null;

  return (
    <section className="summary" aria-label="Import summary">
      <dl className="summary__grid">
        <dt>Product type</dt>
        <dd>{FAMILY_LABELS.glasses}</dd>

        <dt>Who they are for</dt>
        <dd>{AUDIENCE_LABELS[audience]}</dd>

        <dt>Glasses type</dt>
        <dd>{GLASSES_TYPE_LABELS[glassesType]}</dd>

        <dt>Frame shape</dt>
        <dd>{frameShapeLabel(frameShape)}</dd>

        <dt>Folder</dt>
        <dd>{parse.rootName}</dd>

        <dt>Product name</dt>
        <dd>{details.name}</dd>

        <dt>Product code</dt>
        <dd className="mono">{details.productCode}</dd>

        <dt>Description</dt>
        <dd>{details.description}</dd>

        <dt>Sizes</dt>
        <dd>{details.size ? SIZE_LABELS[details.size] : "—"}</dd>

        <dt>Price</dt>
        <dd>
          {details.pricing.onSale ? (
            <>
              <span className="summary__was">
                {base === null ? "—" : formatMoney(base, "ILS")}
              </span>{" "}
              <strong>{sale === null ? "—" : formatMoney(sale, "ILS")}</strong>
              {discount === null ? null : (
                <span className="tag tag--sale">{discount}% off</span>
              )}
            </>
          ) : base === null ? (
            "—"
          ) : (
            formatMoney(base, "ILS")
          )}
        </dd>

        <dt>Colours</dt>
        <dd>
          {colors.length} {colors.length === 1 ? "colour" : "colours"}
        </dd>
      </dl>

      <table className="table summary__colors">
        <thead>
          <tr>
            <th>Colour</th>
            <th>Swatch</th>
            <th>Stock</th>
            {SOURCE_ROLES.map((role) => (
              <th key={role}>{ROLE_LABELS[role]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {colors.map(({ folderName, color }) => {
            const parsed = parse.colors.find((c) => c.folderName === folderName);
            const stock = parseStock(details.stock[folderName] ?? "");
            return (
              <tr key={folderName}>
                <td>
                  <span
                    className="swatch__dot"
                    style={{ backgroundColor: color.swatch_hex }}
                    aria-hidden="true"
                  />{" "}
                  {color.name}
                </td>
                <td className="mono">{color.swatch_hex}</td>
                <td className="num">
                  {stock === 0 ? "0 — out of stock" : (stock ?? "—")}
                </td>
                {SOURCE_ROLES.map((role) => {
                  const source = parsed?.sources.find((s) => s.role === role);
                  return (
                    <td key={role} className="summary__file">
                      {source ? source.file.name : "—"}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="summary__note">
        Starting the import sends {colors.length * SOURCE_ROLES.length} photographs
        to storage and builds the pictures customers will see. Nothing has been sent
        yet.
      </p>

      <div className="row row--wrap">
        <button type="button" className="btn btn--subtle" onClick={onBack}>
          Back
        </button>
        <button type="button" className="btn" onClick={onEdit}>
          Edit
        </button>
        <button
          type="button"
          className="btn btn--primary"
          onClick={onStart}
          disabled={starting}
          data-testid="start-import"
        >
          {starting ? "Starting…" : "Start Import"}
        </button>
      </div>
    </section>
  );
}
